import { getCookie, IGetCookieOptions, setCookie } from '@/modules/cookie';
import { loggerInfo } from '@/modules/logger';
import { getBrowser, getQuery } from '@/modules/utils';
import { isString, isArray } from '@/modules/validate';
import { inApp, getUAgent, isBrowser } from '@/modules/detector';

import { formatLangRegx, setLangConfig } from '@/modules/lang/utils';

import { LangKeys, LangCodeDefault } from '@/data/lang';
import { LogModule } from '@/data/config';
import { HsUtilLang } from '@/data/storage';

export { setLangConfig, LangKeys };

/**
 * 获取UA语言类型
 * @param {string} customUA 自定义传入UA判断
 * @param {string} lang 默认值
 * @returns
 */
export const getLangCodeUAgent = (customUA?: string, lang?: string) => {
  const { ua } = getUAgent(customUA);
  const langCodesRegx = formatLangRegx({
    isStrictStart: false,
    isStrictEnd: false,
  });

  const regxSource = langCodesRegx.source;

  const result = new RegExp(`lang/${regxSource}`, 'i').exec(ua);
  return result ? result[1] : lang;
};

const getClientQuery = (arr: string[]): { [key: string]: unknown } => {
  return arr.reduce((pre: { [key: string]: unknown }, key) => {
    const _value = getQuery(key);
    // 判断是否存在
    if (_value && isString(_value)) {
      pre[key] = _value;
    }
    return pre;
  }, {});
};

type ILangCodeUrl = {
  host: string;
  query: { [key: string]: unknown };
  pathname?: string;
  defaultLang?: string;
};

/**
 * 通过Url获取语言
 * @param {string} lang 默认值
 * @returns
 */
export const getLangCodeUrl = (
  options: ILangCodeUrl,
  otherOpts?: {
    isNeedGetPath: boolean;
  }
): string | LangCodeDefault | undefined => {
  const langCodesRegx = formatLangRegx();

  const locale = options.query['locale'];
  if (locale && isString(locale) && langCodesRegx.test(locale)) return locale;

  const _lang = options.query['_lang'];
  if (_lang && isString(_lang) && langCodesRegx.test(_lang)) return _lang;

  const lang = options.query['lang'];
  if (lang && isString(lang) && langCodesRegx.test(lang)) return lang;

  // 如果是hk默认返回繁体，否则返回简体
  // 兼容一下传入http的情况
  if (/^https?:\/\/hk|^hk/.test(options.host)) return LangCodeDefault.zhHK;

  /**
   * 有传pathname, 则判断一下路径是否带上
   * 目前用在沙特官网
   */
  if (options.pathname) {
    const langPathRegx = formatLangRegx({
      start: '/',
      end: '/',
    });

    const result = options.pathname.match(langPathRegx);

    if (isArray(result) && result.length) {
      /**
       * /zh_cn/
       */
      const data = result[0].replace(/\//g, '');
      return data;
    }
  }

  return options.defaultLang;
};

/**
 * 獲取 lang cookie
 * @param lang
 * @returns
 */
const getLangCodeCookie = (name = HsUtilLang, options?: IGetCookieOptions) => {
  return getCookie<string>(name, options);
};

/**
 * 設置 lang cookie
 * @param value
 * @param name
 */
const setLangCodeCookie = (value: string, name = HsUtilLang) => {
  if (isString(value)) {
    setCookie(name, value);
  }
};

/**
 * 判断是否是简体
 * @param {string} lang
 */
export function isZhCN(lang: string) {
  return isLang(lang, [LangKeys.zhCN]);
}

/**
 * 判断是否是繁体
 * @param {string} lang
 */
export function isZhHK(lang: string) {
  return isLang(lang, [LangKeys.zhHK]);
}

/**
 * 判断是否是英文
 * @param {string} lang
 */
export function isEnUS(lang: string) {
  return isLang(lang, [LangKeys.enUS]);
}

/**
 * 判断是否是阿语
 * @param {string} lang
 */
export function isArSA(lang: string) {
  return isLang(lang, [LangKeys.arSA]);
}

/**
 * 判断是否马来语
 */
export function isMsMy(lang: string) {
  return isLang(lang, [LangKeys.msMy]);
}

/**
 * 判断是否是该语言
 * @param {string} lang
 * @param {string} LangKeys 从工具库获取语言类型 LangKeys
 */
export function isLang(lang: string, langConfig: string[]) {
  const regx = formatLangRegx({
    langConfig,
  });
  return regx.test(lang);
}

interface ILangCode {
  zhCN: LangCodeDefault | string;
  enUS: LangCodeDefault | string;
  zhHK: LangCodeDefault | string;
  arSA: LangCodeDefault | string;
  msMY: LangCodeDefault | string;
  [key: string]: any;
}

export type ILangOptionsKeys = string;
type ILangOptions = ILangCode & {
  defaultKey?: ILangOptionsKeys;
  lang?: string;
};
const formatLangCode = (langOptions: ILangOptions): LangCodeDefault | string => {
  const defaultLang = langOptions?.defaultKey
    ? langOptions?.[langOptions?.defaultKey] ?? langOptions?.defaultKey
    : langOptions.zhCN;

  loggerInfo(['默认语言', defaultLang], LogModule.Lang);

  if (!langOptions?.lang) {
    return defaultLang;
  }
  // 判断是语言类型
  // 繁体
  if (isZhHK(langOptions.lang)) {
    return langOptions.zhHK;
  }
  // 英文
  if (isEnUS(langOptions.lang)) {
    return langOptions.enUS;
  }

  // 简体
  if (isZhCN(langOptions.lang)) {
    return langOptions.zhCN;
  }

  // 阿拉伯语
  if (isLang(langOptions.lang, [LangKeys.arSA])) {
    return langOptions.arSA;
  }

  // 马来语
  if (isLang(langOptions.lang, [LangKeys.msMy])) {
    return langOptions.msMY;
  }

  // 默认简体
  // 支持传入Key, 也可以自定义默认值
  // 否则系统自带

  return defaultLang || (isBrowser() ? getBrowser().navigator.language : '');
};

/**
 * 客户端调用的方法
 * 获取当前语言，优先获取地址，然後cookies值，然后获取UA, 其次获取默认值
 * 如果不传入 isNeedGetUrl， isNeedSetCookie，isNeedGetCookie，则取默认值，默认值 是否在APP内
 * 站外通过，通过cookies 判断
 * 站内通过，不通过cookies
 * @param {object} langOptions
 * @param {string} zhCN 简体默认值
 * @param {string} zhHK 繁体默认值
 * @param {string} enUS 英文默认值
 * @param {string} arSA 阿拉伯语默认值
 * @param {string} msMy 马来语默认值
 * @param {string} defaultKey 默认返回值【支持传入Key, 也可以自定义默认值】
 * @param {boolean} isNeedGetUrl 是否需要通過 Url 判斷
 * @param {boolean} isNeedGetPath 是否需要通過 Url Path获取， 开启的前提 isNeedGetUrl 需要为true
 * @param {boolean} isNeedSetCookie 是否需要通過 cookie 判斷
 * @param {boolean} isNeedGetCookie 是否需要設置 cookie 判斷
 * @returns
 */
export const getLangCode = (
  options?: Partial<ILangCode> & {
    isNeedSetCookie?: boolean;
    isNeedGetCookie?: boolean;
    isNeedGetUrl?: boolean;
    isNeedGetPath?: boolean;
  }
): string => {
  const _options = {
    zhCN: LangCodeDefault.zhCN,
    zhHK: LangCodeDefault.zhHK,
    enUS: LangCodeDefault.enUS,
    arSA: LangCodeDefault.arSA,
    msMY: LangCodeDefault.msMY,
    isNeedSetCookie: true,
    isNeedGetCookie: true,
    isNeedGetUrl: true,
    isNeedGetPath: true,
    /** 默认简体 */
    defaultKey: 'zhCN' as ILangOptionsKeys,
    ...options,
  };
  // 参数处理
  // 站外通过，通过cookies 判断
  // 站内通过，不通过cookies
  const _inApp = inApp();
  const isNeedGetCookie = options?.isNeedGetCookie ?? !_inApp;
  const isNeedSetCookie = options?.isNeedSetCookie ?? !_inApp;
  const isNeedGetUrl = options?.isNeedGetUrl ?? !_inApp;
  const isNeedGetPath = options?.isNeedGetPath ?? false;

  // 获取地址上的Url链接
  const urlLangCode = getLangCodeUrl({
    host: window.location.host, // 当前的域名
    // defaultLang: , // 默认值
    query: getClientQuery(['locale', '_lang', 'lang']),
    pathname: isNeedGetPath ? window.location.pathname : '',
  });
  // 获取cookie上的參數
  const cookieLangCode = getLangCodeCookie();
  // 获取UA上的链接
  const uAgentLangCode = getLangCodeUAgent();

  const { zhCN, zhHK, enUS, arSA, msMY, defaultKey } = _options;

  const lang = formatLangCode({
    lang:
      (isNeedGetUrl && urlLangCode ? urlLangCode : null) ||
      (isNeedGetCookie ? cookieLangCode : null) ||
      uAgentLangCode ||
      '',
    zhCN,
    zhHK,
    enUS,
    arSA,
    msMY,
    defaultKey,
  });

  isNeedSetCookie && setLangCodeCookie(lang);

  return lang;
};

/**
 * 服务端调用的方法
 * 获取当前语言，优先获取地址参数，地址路径，然後cookies值, 然后获取UA, 其次获取默认值
 * 如果不传入 isNeedGetPath, isNeedGetUrl，isNeedGetCookie，则取默认值，默认值 是否在APP内
 * 根据传入的ua来判断
 * @param {object} langOptions
 * @param {string} zhCN 简体默认值
 * @param {string} zhHK 繁体默认值
 * @param {string} enUS 英文默认值
 * @param {string} arSA 阿拉伯语默认值
 * @param {string} cookie 默認值 空字符串
 * @param {string} isNeedGetCookie 默認值 true 判断是否需要从 Cookie 获取
 * @param {string} isNeedGetUrl 默認值 true 判断是否需要从 Url 获取
 * @param {string} isNeedGetPath 默認值 false 开启的前提 isNeedGetUrl 需要为true
 * @param {string} defaultKey 默認值 默认返回值 默认简体
 * @returns
 */
export function getLangCodeByServer(options: {
  host: string;
  query?: ICommonObject;
  ua?: string;
  langOptions?: Partial<ILangOptions>;
  cookies?: string;
  isNeedGetCookie?: boolean;
  isNeedGetUrl?: boolean;
  defaultKey?: ILangOptionsKeys;
  isNeedGetPath?: boolean;
  pathname?: string;
}) {
  // 参数处理
  // 站外通过，通过cookies 判断
  // 站内通过，不通过cookies
  const _inApp = inApp({ customUA: options?.ua });

  const _isNeedGetCookie = options?.isNeedGetCookie ?? !_inApp;
  const _isNeedGetUrl = options?.isNeedGetUrl ?? !_inApp;

  const _isNeedGetPath = options?.isNeedGetPath ?? false;

  const urlLangCode = getLangCodeUrl({
    host: options?.host,
    query: options?.query ?? {},
    pathname: _isNeedGetPath ? options?.pathname : '',
  });

  // 获取cookie上的參數
  const cookieLangCode =
    _isNeedGetCookie && options?.cookies
      ? getLangCodeCookie(undefined, {
          cookies: options.cookies,
        })
      : undefined;

  const uAgentLangCode = getLangCodeUAgent(options?.ua);

  const _langOptions = {
    zhCN: LangCodeDefault.zhCN,
    zhHK: LangCodeDefault.zhHK,
    enUS: LangCodeDefault.enUS,
    arSA: LangCodeDefault.arSA,
    msMY: LangCodeDefault.msMY,
    ...(options?.langOptions ?? {}),
  };

  /** 默认简体 */
  const _defaultKey =
    options?.defaultKey ?? options?.langOptions?.defaultKey ?? ('zhCN' as ILangOptionsKeys);

  loggerInfo(
    [
      'getLangCodeByServer方法',
      {
        lang: (_isNeedGetUrl ? urlLangCode : null) || cookieLangCode || uAgentLangCode || '',
        ..._langOptions,
        defaultKey: _defaultKey,
      },
    ],
    LogModule.Lang
  );

  const lang = formatLangCode({
    lang: (_isNeedGetUrl ? urlLangCode : null) || cookieLangCode || uAgentLangCode || '',
    ..._langOptions,
    defaultKey: _defaultKey,
  });

  return lang;
}
